import { validateAcls } from '@redviking/argonaut-core-ui/src/auth';
import {
    EntityTypeMap,
    type ValidationResult,
} from 'types';
import {
    GetAllAttributeSettingsForMaterialUsingMaterialModelIdDocument,
    GetMaterialUdfColumnsDocument,
    GetSnFormatDocument,
} from 'types/db';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import type { MaterialEntity } from './material.entity';
import type { AttributeSettingForAttributeLinker } from '@redviking/argonaut-util/types/attribute-settings/attribute-settings-config.zod';
import { validateAttributeSettingHasBeenFufilled } from 'src/components/AttributeSettings/attributeSettings.validations';
import { validateUdfData } from 'src/udf/udf.validations';

export function validateMaterialSelectAcl (): boolean {
    return validateAcls([ 'material_select' ]);
}

export function validateMaterialUpdateAcl (): boolean {
    return validateAcls([ 'material_update' ]);
}

async function validateMaterialUdfData (entityData: EntityTypeMap['material']): Promise<ValidationResult> {
    const { argo_material_udf_schema } = await gqlClient.request({
        document: GetMaterialUdfColumnsDocument,
        variables: {
            materialModelId: entityData.materialModel.id,
        },
    });

    const columns = argo_material_udf_schema.map(i => i.udf_column);

    for (const column of columns) {
        if (column) {
            const data = entityData.udfData.find(i => i.udf_column_id === column.id);
            if (!data && column.validation_required) {
                return {
                    status: 'error',
                    errorMessage: `Missing required field for UDF Column ${column.name}`,
                };
            }


            if (data) {
                const udfResult = validateUdfData(data, column);
                if (udfResult.status !== 'success') {
                    return udfResult;
                }
            }
        }
    }
    return {
        status: 'success',
    };
}

const validateAttributeSettingsHaveBeenFufilled = async (entity: MaterialEntity, ignoreSchemaWarning: boolean): Promise<ValidationResult> => {
    const {
        argo_material_model_by_pk,
    } = await gqlClient.request({
        document: GetAllAttributeSettingsForMaterialUsingMaterialModelIdDocument,
        variables: {
            materialModelId: entity.materialModel.id,
        },
    });
    if (!argo_material_model_by_pk) {
        return {
            status: 'error',
            errorMessage: `Model Number with id ${entity.materialModel.id} not found when querying for attribute settings`,
        };
    }

    const attributeSettings = [
        ...argo_material_model_by_pk.material_class.material_class_attribute_settings,
    ];
    const materialModelAttributeSettingUseDefaults = argo_material_model_by_pk.meta.attributeSettings || {};
    for (const attributeSettingFromDB of attributeSettings) {
        const modelAttributeSettingUseDefault = materialModelAttributeSettingUseDefaults[attributeSettingFromDB.id];
        if (modelAttributeSettingUseDefault === undefined) {
            if (attributeSettingFromDB.config.active) {
                const attributeSetting: AttributeSettingForAttributeLinker = {
                    ...attributeSettingFromDB,
                    attributeSchema: {
                        ...attributeSettingFromDB.attribute_schema,
                        properties: attributeSettingFromDB.attribute_schema.attribute_schema_definitions,
                    },
                };
                const result = validateAttributeSettingHasBeenFufilled(attributeSetting, entity.attributeIds);
                if (result.type === 'error') {
                    return {
                        status: 'error',
                        errorMessage: result.messagePlaintext,
                    };
                } else if (result.type === 'warning' && !ignoreSchemaWarning) {
                    return {
                        status: 'modal',
                        modalComponent: () => import(/* webpackChunkName: "material" */ './MaterialSaveWarning.view.vue').then(m => m.default),
                    };
                }
            }
        } else if (modelAttributeSettingUseDefault) {
            const attributeSetting: AttributeSettingForAttributeLinker = {
                ...attributeSettingFromDB,
                attributeSchema: {
                    ...attributeSettingFromDB.attribute_schema,
                    properties: attributeSettingFromDB.attribute_schema.attribute_schema_definitions,
                },
            };
            const result = validateAttributeSettingHasBeenFufilled(attributeSetting, entity.attributeIds);
            if (result.type === 'error') {
                return {
                    status: 'error',
                    errorMessage: result.messagePlaintext,
                };
            } else if (result.type === 'warning' && !ignoreSchemaWarning) {
                return {
                    status: 'modal',
                    modalComponent: () => import(/* webpackChunkName: "material" */ './MaterialSaveWarning.view.vue').then(m => m.default),
                };
            }
        }
    }
    return {
        status: 'success',
    };
};

export async function validateMaterial (material: MaterialEntity): Promise<ValidationResult> {
    if (material.snCfg.kind === 'direct') {
        if (!material.snCfg.serialNumber) {
            return {
                status: 'error',
                errorMessage: 'A Serial Number is Required',
            };
        }
    } else {
        const {
            argo_sn_format,
        } = await gqlClient.request({
            document: GetSnFormatDocument,
            variables: {
                snFormatId: material.snCfg.id,
            },
        });

        const snConfigFromFormat = argo_sn_format[0].config;
        for (const token of snConfigFromFormat.tokens.filter(t => t.type === 'input')) {
            const tokenValueFromEntity = material.snCfg.tokens.find(t => t.name === token.name);
            if (!tokenValueFromEntity) {
                return {
                    status: 'error',
                    errorMessage: `Missing Serial Number Token Value for token: ${token.name}. Please ensure all tokens are present.`,
                };
            }
            if (!tokenValueFromEntity.value) {
                return {
                    status: 'error',
                    errorMessage: `Missing value for Serial Number Token: ${token.name}. Please provide a value.`,
                };
            }
            if (token.validation && !new RegExp(`^${token.validation}$`, 'u').test(tokenValueFromEntity.value)) {
                const message = `Input for ${token.name} doesnt pass validation ^${token.validation}$`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
        }
    }

    const materialUdfDataResult = await validateMaterialUdfData(material);
    if (materialUdfDataResult.status !== 'success') {
        return materialUdfDataResult;
    }

    const status = await validateAttributeSettingsHaveBeenFufilled(material, material.ignoreSchemaWarning);
    if (status.status !== 'success') {
        return status;
    }

    return {
        status: 'success',
    };
}

